import React, { useState, type JSX } from 'react';
import arrowDown from '@/assets/svg/static/arrowDown.svg';
import originalIcon from '@/assets/svg/static/original.svg';
import sizeSvg from '@/assets/svg/static/size.svg';
import { ProductOfferStrip, SizePills } from '@/components/ui';
import { formatIndianPrice, toTitleCase } from '@/utils/formatting';
import InfluencerReelCard from '../InfluencerReelCard';
import styles from './ProductDetailInfo.module.css';
import type { ProductDetailInfoProps } from './ProductDetailInfo.types';

export const ProductDetailInfo = React.memo(
  ({
    productDetails,
    selectedSize,
    sizeError,
    onSizeSelect,
    onViewSizeChart,
    description,
    influencerVideos,
    onReelClick,
  }: ProductDetailInfoProps): JSX.Element => {
    const [activeAccordion, setActiveAccordion] = useState<{
      description: boolean;
    }>({
      description: false,
    });

    const toggleAccordion = (): void => {
      setActiveAccordion((prev) => ({
        ...prev,
        description: !prev.description,
      }));
    };

    const cleanProductName = (
      brand?: string,
      productName?: string
    ): string | undefined => {
      if (!brand || !productName) return productName;
      try {
        const regex = new RegExp(brand, 'gi');
        return productName.replace(regex, '').trim();
      } catch (err) {
        console.error(err);
        return productName;
      }
    };

    const mrpVal = parseFloat(productDetails.price || '0');
    const discountVal = parseFloat(productDetails.discountPrice || '0');
    let discountPercentage = 0;
    if (mrpVal > 0 && discountVal > 0 && discountVal < mrpVal) {
      discountPercentage = Math.round(((mrpVal - discountVal) / mrpVal) * 100);
    }

    return (
      <div className={styles.productInfo}>
        {/* Brand Name & Verify Badge */}
        <div className={styles.brandNameRow}>
          <img
            src={originalIcon}
            className={styles.verifiedCheckIcon}
            alt="Verified Brand"
          />
          <span className={styles.brandName}>
            {toTitleCase(productDetails.brand)}
          </span>
        </div>

        <h2 className={styles.productName}>
          {toTitleCase(
            cleanProductName(
              productDetails.brand ?? '',
              productDetails.name ?? ''
            ) ?? ''
          )}
        </h2>

        {/* Pricing Info */}
        <div className={styles.priceContainer}>
          <span className={styles.price}>
            ₹{formatIndianPrice(String(productDetails.discountPrice))}
          </span>
          {productDetails.price &&
            productDetails.price !== productDetails.discountPrice && (
              <>
                <span className={styles.mrp}>
                  ₹{formatIndianPrice(String(productDetails.price))}
                </span>
                {discountPercentage > 0 && (
                  <span className={styles.discountPercent}>
                    {discountPercentage}% off
                  </span>
                )}
              </>
            )}
        </div>

        {/* Product Offer Strip */}
        {(productDetails.brandOffer || productDetails.pricingOffer) && (
          <ProductOfferStrip
            brandOffer={productDetails.brandOffer}
            pricingOffer={productDetails.pricingOffer}
          />
        )}

        <div className={styles.dividerLine} />

        {/* Size Section */}
        {productDetails.availableSizes && (
          <div className={styles.sizeSection}>
            <div className={styles.sizeSectionHeader}>
              <span className={styles.sizeTitle}>SIZE</span>
            </div>

            <div className={styles.sizeContainer}>
              <SizePills
                availableSizes={productDetails.availableSizes}
                selectedSize={selectedSize}
                onSizeSelect={onSizeSelect}
              />
              {sizeError && (
                <p
                  style={{
                    color: '#FF3C6E',
                    fontSize: '12px',
                    fontWeight: 500,
                    marginTop: '8px',
                  }}
                >
                  Please select a size to continue
                </p>
              )}
              <button
                className={styles.viewSizeChartBtn}
                onClick={onViewSizeChart}
              >
                <img
                  src={sizeSvg}
                  className={styles.sizeChartIcon}
                  alt="Ruler Icon"
                />
                <span>VIEW SIZE CHART</span>
              </button>
            </div>
            <div className={styles.dividerLine} />
          </div>
        )}

        {/* Collapsible Details */}
        {description && (
          <div>
            <div className={styles.accordionHeader} onClick={toggleAccordion}>
              <span>Product Detail</span>
              <img
                src={arrowDown}
                className={`${styles.accordionChevron} ${activeAccordion.description ? styles.chevronRotate : ''}`}
                alt="Chevron"
              />
            </div>
            {activeAccordion.description && (
              <div className={styles.accordionContent}>
                {description.replace(/<[^>]*>/g, '')}
              </div>
            )}
            <div className={styles.dividerLine} />
          </div>
        )}

        {/* Top Influencer Picks */}
        <div
          id="influencer-picks-section"
          className={styles.influencerPicksSection}
        >
          <h3 className={styles.influencerTitle}>TOP INFLUENCER PICKS</h3>
          <div className={styles.influencerScroll}>
            {influencerVideos.map((video, idx) => (
              <InfluencerReelCard
                key={video.id}
                video={video}
                onClick={() => onReelClick(idx)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
);

export default ProductDetailInfo;
